import React, { useEffect, useRef, useState } from 'react';
import Form, { Field, useForm } from 'rc-field-form';
import msdkUtil from 'lib/utils/MSDK';
import channelService from 'lib/services/channelService';
import { GameConfigData, GameConfig, loadGameConfig } from 'lib/utils/loadScript';
import { fetchAMSRoleList, fetchMSDKDecrypt } from 'lib/apis';
import { GameRoleType, RoleInfoType, ShowMobileGameRoleParams } from 'lib/types/AMS';
import { AMS_CHANNEL, AMS_SYSTEM } from 'lib/constants/ams';
import { isiOS } from 'lib/utils/util';
import toast from 'lib/utils/toast';
import { report, setReportEventRefer } from 'lib/utils/dataReport';
import { ATTA_EVENT_RESULT, ATTA_REPORT } from 'lib/constants/atta';
import { closeModal, showModal } from '../Modal';
import Dialog from '../Dialog';
import './index.scss';

type FormValues = {
  amsChannelId: string;
  amsSystemId: string;
  amsPartitionId: string;
  roleId: string;
};

const MobileGameRole = ({ confirmCallback, closeCallback }) => {
  const { gameCode } = channelService;
  const [form] = useForm();
  const [systemOptions, setSystemOptions] = useState<GameConfigData[]>();
  const [channelOptions, setChannelOptions] = useState<GameConfigData[]>();
  const [partitionOptions, setPartitionOptions] = useState<GameConfigData[]>();
  const [roleList, setRoleList] = useState<RoleInfoType[]>([]);
  const autoQueryRoleCondition = useRef<string[]>([]); // 满足自动查询角色的条件
  const filterPartitionList = useRef<GameConfigData[]>([]);
  const init = useRef(false);

  useEffect(() => {
    fetchGameConfig();
  }, []);

  const getMSDKChannel = async (channelOptions: GameConfigData[]) => {
    let channelId = msdkUtil.MSDKChannel;
    const isExistChannel = channelOptions?.filter((c) => c.value === `${channelId}`) || [];
    // 如果不存在的话 需要调用解密方法，获取到登录类型，2表示QQ 1表示微信
    if (!isExistChannel.length) {
      const res = await fetchMSDKDecrypt();
      if (res?.code !== 0) return;
      const { accType } = res.data || {};
      // 这里的意义是为了适配麻将的微信、qq分别为11，12，所以通过解密msdk返回微信还是QQ，根据值进行映射字符串 1:'weixin' 2:'qq'
      const channelData = channelOptions?.find((c) => c.key === AMS_CHANNEL[accType]);
      channelId = channelData?.value;
    }
    return channelId;
  };

  const fetchGameConfig = async () => {
    const res = await loadGameConfig(gameCode);
    if (!res) return;
    const { systemData, channelData, partitionData } = res || {};
    setSystemOptions(systemData);
    setChannelOptions(channelData);
    filterPartitionList.current = partitionData;
    setPartitionOptions(partitionData);
    initGameRoleForm(res);
  };

  // 初始化表单
  const initGameRoleForm = async ({ systemData, channelData, partitionData }: GameConfig) => {
    autoQueryRoleCondition.current = [];

    // 无系统/渠道配置（如 speed 仅 STD_DATA）：用 MSDK 补全 AMS 接口所需的 plat/area，自动查询只需区服
    if (partitionData?.length && !systemData?.length && !channelData?.length) {
      form.setFieldValue('amsSystemId', isiOS() ? AMS_SYSTEM.IOS : AMS_SYSTEM.ANDROID);
      form.setFieldValue('amsChannelId', String(msdkUtil.MSDKChannel));
      form.setFieldValue('amsPartitionId', msdkUtil.MSDKPartition || partitionData[0]?.value);
      autoQueryRoleCondition.current.push('amsPartitionId');
    } else {
      if (systemData?.length) {
        autoQueryRoleCondition.current.push('amsSystemId');
        form.setFieldValue('amsSystemId', isiOS() ? AMS_SYSTEM.IOS : AMS_SYSTEM.ANDROID);
      }

      if (channelData?.length) {
        autoQueryRoleCondition.current.push('amsChannelId');
        const channelId = await getMSDKChannel(channelData);
        form.setFieldValue('amsChannelId', channelId);
      }

      if (partitionData?.length) {
        autoQueryRoleCondition.current.push('amsPartitionId');
        form.setFieldValue('amsPartitionId', msdkUtil.MSDKPartition);
      }
    }

    const formData = form.getFieldsValue(true);
    const canAutoQuery = autoQueryRoleCondition.current.every((c) => formData[c]);

    if (canAutoQuery) {
      report(ATTA_REPORT.GAME_ROLE.VIEW, {
        eventResult: 1,
      });
      queryAMSGameRole(formData, true);
    }
    init.current = true;
  };

  const queryAMSGameRole = async (data, isAuto = false) => {
    toast.loading();
    const res = await fetchAMSRoleList({
      gameCode,
      ...data,
    });
    toast.close();
    if (res?.code !== 0) {
      report(ATTA_REPORT.GAME_ROLE.FIND_FAIL, {
        eventResult: isAuto ? ATTA_EVENT_RESULT.SUCCESS : res?.code,
      });
      return;
    }

    const roles: RoleInfoType[] =
      res?.data?.map((item) => ({
        roleName: item.name,
        roleId: item.id,
      })) || [];

    if (roles?.length === 0) {
      report(ATTA_REPORT.GAME_ROLE.FIND_FAIL, {
        eventResult: isAuto ? ATTA_EVENT_RESULT.SUCCESS : ATTA_EVENT_RESULT.FAIL,
      });
      toast.info('暂未查询到游戏角色');
    }
    report(ATTA_REPORT.GAME_ROLE.FIND_SUCCESS, {
      eventResult: isAuto ? ATTA_EVENT_RESULT.SUCCESS : ATTA_EVENT_RESULT.FAIL,
    });
    setRoleList([...roles]);
    form.setFieldValue('roleId', roles[0]?.roleId);
  };

  // rc-field-form 的 onChange 先于 store 写入，需手动合并当前选中值
  const changeGameRole = (fieldName: keyof FormValues, value: string) => {
    const isPartition = fieldName === 'amsPartitionId';
    if (!isPartition) {
      form.resetFields(['amsPartitionId']);
    }

    const formData = { ...form.getFieldsValue(true), [fieldName]: value };
    const canAutoQuery = autoQueryRoleCondition.current.every((c) => formData[c]);
    if (canAutoQuery) {
      queryAMSGameRole(formData);
    } else {
      setRoleList([]);
      form.resetFields(['roleId']);
      if (!isPartition) {
        form.resetFields(['amsPartitionId']);
      }
    }
  };

  // 点击确认
  const confirm = async () => {
    form.submit();
  };

  // 表单提交
  const confirmGameRole = (res: GameRoleType) => {
    const roleInfo = roleList.find((c) => c?.roleId === res.roleId);
    const partitionInfo = filterPartitionList.current.find((c) => c.value === res.amsPartitionId) as GameConfigData;
    const amsData: GameRoleType = {
      ...res,
      roleName: roleInfo?.roleName,
      amsPartitionName: partitionInfo?.label,
    };
    closeModal();
    report(ATTA_REPORT.GAME_ROLE.CONFIRM);
    setReportEventRefer('');
    confirmCallback?.(amsData);
  };

  const submitFail = (err) => {
    const errMsg = err.errorFields[0]?.errors[0];
    toast.info(errMsg);
  };

  const close = () => {
    setReportEventRefer('');
    report(ATTA_REPORT.GAME_ROLE.CANCEL);
    closeModal();
    closeCallback?.();
  };

  return (
    <Dialog
      title="请选择区服角色领取礼包"
      customClass="game-role__dialog"
      keyboard={{ onEnter: confirm, onEscape: close }}
    >
      <Form form={form} onFinish={confirmGameRole} onFinishFailed={submitFail}>
        <div className="game-role">
          {channelOptions?.length ? (
            <div className="game-role__item">
              <div className="game-role__item--title">选择渠道</div>
              <div className="game-role__item--picker">
                <Field<FormValues>
                  name="amsChannelId"
                  rules={[
                    {
                      required: true,
                      message: '请选择渠道',
                    },
                  ]}
                >
                  <select
                    className="picker-list"
                    data-select-type="channel"
                    onChange={(e) => changeGameRole('amsChannelId', e.target.value)}
                  >
                    <option value="">请选择</option>
                    {channelOptions?.map((item) => (
                      <option label={item.label} value={item.value} key={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <div className="picker-arrow"></div>
              </div>
            </div>
          ) : null}
          {systemOptions?.length ? (
            <div className="game-role__item">
              <div className="game-role__item--title">选择系统</div>
              <div className="game-role__item--picker">
                <Field<FormValues>
                  name="amsSystemId"
                  rules={[
                    {
                      required: true,
                      message: '请选择系统',
                    },
                  ]}
                >
                  <select
                    className="picker-list"
                    data-select-type="system"
                    onChange={(e) => changeGameRole('amsSystemId', e.target.value)}
                  >
                    <option value="">请选择</option>
                    {systemOptions?.map((item) => (
                      <option label={item.label} value={item.value} key={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <div className="picker-arrow"></div>
              </div>
            </div>
          ) : null}

          {partitionOptions?.length ? (
            <div className="game-role__item">
              <div className="game-role__item--title">选择服务器</div>
              <div className="game-role__item--picker">
                <Field
                  shouldUpdate={(prev: any, cur: any) =>
                    prev.amsSystemId !== cur.amsSystemId || prev.amsChannelId !== cur.amsChannelId
                  }
                >
                  {(control, meta, { getFieldValue }) => {
                    const systemId = getFieldValue('amsSystemId');
                    const channelId = getFieldValue('amsChannelId');
                    const hasVisiblePickers = Boolean(systemOptions?.length || channelOptions?.length);
                    let partitions = [...partitionOptions];
                    if (hasVisiblePickers && !systemId && !channelId) {
                      partitions = [];
                    }

                    if (systemId) {
                      partitions = partitions.filter((c) => !c.amsSystemId || c.amsSystemId === systemId);
                    }

                    if (channelId) {
                      partitions = partitions.filter((c) => !c.amsChannelId || c.amsChannelId === channelId);
                    }

                    return (
                      <Field<FormValues>
                        name="amsPartitionId"
                        rules={[
                          {
                            required: true,
                            message: '请选择服务器',
                          },
                        ]}
                      >
                        <select
                          className="picker-list"
                          data-select-type="server"
                          onChange={(e) => changeGameRole('amsPartitionId', e.target.value)}
                        >
                          <option value="">请选择</option>
                          {partitions?.map((item) => (
                            <option label={item.label} value={item.value} key={item.value}>
                              {item.label}
                            </option>
                          ))}
                        </select>
                      </Field>
                    );
                  }}
                </Field>
                <div className="picker-arrow"></div>
              </div>
            </div>
          ) : null}
          <div className="game-role__item">
            <div className="game-role__item--title">选择角色</div>
            <div className="game-role__item--picker">
              <Field<FormValues>
                name="roleId"
                rules={[
                  {
                    required: true,
                    message: '请选择角色',
                  },
                ]}
              >
                <select className="picker-list" data-select-type="role">
                  <option value="">请选择</option>
                  {roleList?.map((item) => (
                    <option label={item.roleName} value={item.roleId} key={item.roleName}>
                      {item.roleName}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="picker-arrow"></div>
            </div>
          </div>
          <div className="game-role__tips">
            <img
              className="game-role__tips--icon"
              src="https://obd.gtimg.com/flow/static/assets/8a2f8e7463324881bad7f0476b0fc49da4e057f8.svg"
            />
            <div>请依次选择渠道、系统、服务器、角色</div>
          </div>
        </div>
        <div className="modal-dialog__footer">
          <div className="modal-dialog__footer--btn close" onClick={close}>
            取消
          </div>
          <div className="modal-dialog__footer--btn confirm" onClick={confirm}>
            确认领取
          </div>
        </div>
      </Form>
    </Dialog>
  );
};

export const getMobileGameRole = (params) => <MobileGameRole {...params} />;

export const showMobileGameRole = ({ confirmCallback, closeCallback }: ShowMobileGameRoleParams) =>
  showModal(<MobileGameRole confirmCallback={confirmCallback} closeCallback={closeCallback} />);
export default MobileGameRole;
